import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc, collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { IS_MOCK_DEMO, USAGE_CATEGORIES } from '../constants';

const BILLING_API_BASE = import.meta.env.VITE_BILLING_API_BASE || '';

// User-facing plan/pack catalog (tax-inclusive). Mirrors billing-functions/src/catalog.js.
const SUBSCRIPTION_PLANS = [
  { key: 'light', label: 'Light', price: '¥500 / 月', credits: 2000 },
  { key: 'standard', label: 'Standard', price: '¥1,200 / 月', credits: 5500 },
  { key: 'deep', label: 'Deep', price: '¥2,500 / 月', credits: 15000 },
];

const CREDIT_PACKS = [
  { key: 'small', label: 'Small', price: '¥300', credits: 1500 },
  { key: 'medium', label: 'Medium', price: '¥1,000', credits: 5500 },
  { key: 'large', label: 'Large', price: '¥2,000', credits: 12000 },
];

const STATUS_LABELS = {
  active: { text: '利用中', cls: 'bg-emerald-900/40 text-emerald-300 border-emerald-700' },
  trialing: { text: 'トライアル中', cls: 'bg-blue-900/40 text-blue-300 border-blue-700' },
  past_due: { text: '支払い確認中', cls: 'bg-amber-900/40 text-amber-300 border-amber-700' },
  canceled: { text: '解約済み', cls: 'bg-gray-800 text-gray-400 border-gray-700' },
  unpaid: { text: '未払い', cls: 'bg-rose-900/40 text-rose-300 border-rose-700' },
};

const APP_USAGE_CATEGORIES = [
  { key: 'sink', label: 'Sink', color: 'bg-sky-500', sources: ['web', 'sink'] },
  { key: 'prob', label: 'Prob', color: 'bg-fuchsia-500', sources: ['prob'] },
];

const OTHER_APP_USAGE = { key: 'other', label: 'Other', color: 'bg-gray-500' };

const FALLBACK_APP_COLORS = [
  'bg-teal-500',
  'bg-rose-500',
  'bg-lime-500',
  'bg-cyan-500',
  'bg-violet-500',
  'bg-orange-500',
];

const PROB_USAGE_CATEGORIES = [
  { key: 'prob_grounding', label: 'Grounding', actions: ['prob_grounding'], color: 'bg-fuchsia-500' },
  { key: 'prob_scenario_unlock', label: 'Scenario', actions: ['prob_scenario_unlock'], color: 'bg-pink-500' },
  { key: 'prob_article_predict', label: 'Predict', actions: ['prob_article_predict'], color: 'bg-rose-500' },
];

function toDate(ts) {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate();
  if (typeof ts === 'string') return new Date(ts);
  if (ts.seconds) return new Date(ts.seconds * 1000);
  return null;
}

function fmtDate(ts) {
  const d = toDate(ts);
  return d ? d.toISOString().split('T')[0] : '—';
}

function formatCredits(value) {
  return Number(value || 0).toLocaleString();
}

function lotSourceLabel(source) {
  if (source === 'credit_pack') return 'クレジットパック';
  if (source === 'subscription_grant') return '月次付与';
  return 'クレジット';
}

function getLotCreditBreakdown(lot) {
  const remaining = Math.max(0, Number(lot.remainingCredits || 0));
  const used = Math.max(0, Number(lot.usedCredits || 0));
  const granted = Math.max(remaining + used, Number(lot.grantedCredits || 0));
  return { remaining, used, granted };
}

function labelFromKey(key) {
  if (!key) return 'Other';
  return key
    .split(/[_-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function hashString(value) {
  return [...value].reduce((hash, char) => ((hash * 31) + char.charCodeAt(0)) >>> 0, 0);
}

function getAppCategory(event) {
  const source = String(event?.appId || event?.sourceApp || event?.source || '').toLowerCase();
  const known = APP_USAGE_CATEGORIES.find((app) => app.sources.includes(source));
  if (known) return known;
  if (!source) return OTHER_APP_USAGE;
  return {
    key: source,
    label: labelFromKey(source),
    color: FALLBACK_APP_COLORS[hashString(source) % FALLBACK_APP_COLORS.length],
  };
}

function getActionCategory(appKey, actionType) {
  const categories = appKey === 'prob' ? PROB_USAGE_CATEGORIES : USAGE_CATEGORIES;
  return categories.find((cat) => cat.actions.includes(actionType)) || {
    key: 'other',
    label: 'Other',
    color: 'bg-gray-500',
  };
}

function buildLotUsageByApp(usageEvents) {
  const lotUsage = new Map();

  for (const event of usageEvents || []) {
    const consumedLots = Array.isArray(event.consumedLots) ? event.consumedLots : [];
    if (consumedLots.length === 0) continue;

    const app = getAppCategory(event);
    const actionType = event.action_type || event.actionType;
    const actionCategory = getActionCategory(app.key, actionType);
    for (const consumed of consumedLots) {
      const lotId = consumed?.lotId;
      const amount = Math.max(0, Number(consumed?.amount || 0));
      if (!lotId || amount <= 0) continue;

      const current = lotUsage.get(lotId) || new Map();
      const appEntry = current.get(app.key) || { ...app, credits: 0, actions: new Map() };
      const actionEntry = appEntry.actions.get(actionCategory.key) || { ...actionCategory, credits: 0 };
      appEntry.credits += amount;
      appEntry.actions.set(actionCategory.key, {
        ...actionEntry,
        credits: actionEntry.credits + amount,
      });
      current.set(app.key, appEntry);
      lotUsage.set(lotId, current);
    }
  }

  return lotUsage;
}

function getLotKnownUsed(usageByApp) {
  return Array.from(usageByApp?.values?.() || []).reduce((sum, app) => sum + app.credits, 0);
}

function getAppLegend(lotUsageByApp, lots) {
  const dynamicApps = new Map();
  for (const appMap of lotUsageByApp.values()) {
    for (const app of appMap.values()) {
      if (
        app.key !== OTHER_APP_USAGE.key
        && !APP_USAGE_CATEGORIES.some((known) => known.key === app.key)
      ) {
        dynamicApps.set(app.key, app);
      }
    }
  }
  const hasUnclassifiedUsage = lots.some((lot) => {
    const { used } = getLotCreditBreakdown(lot);
    return used > getLotKnownUsed(lotUsageByApp.get(lot.id));
  });

  return [
    ...APP_USAGE_CATEGORIES,
    ...dynamicApps.values(),
    ...(hasUnclassifiedUsage ? [OTHER_APP_USAGE] : []),
  ];
}

export default function BillingPanel({ user, isActive, userData, usageEvents = [], loadingUsage = false }) {
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [lots, setLots] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [expandedLotIds, setExpandedLotIds] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!isActive || !user?.uid || IS_MOCK_DEMO) return;
      setLoading(true);
      setError('');
      try {
        const subSnap = await getDoc(doc(db, 'subscriptions', user.uid));
        const lotSnap = await getDocs(query(
          collection(db, 'creditLots'),
          where('ownerUid', '==', user.uid),
          where('status', 'in', ['active', 'depleted', 'expired']),
          orderBy('expiresAt', 'asc'),
        ));
        if (cancelled) return;
        setSubscription(subSnap.exists() ? subSnap.data() : null);
        const allLots = lotSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const activeLots = allLots
          .filter((l) => l.status === 'active')
          .sort((a, b) => (toDate(a.expiresAt)?.getTime() || 0) - (toDate(b.expiresAt)?.getTime() || 0));
        const historyLots = allLots
          .filter((l) => l.status !== 'active')
          .sort((a, b) => (toDate(b.expiresAt)?.getTime() || 0) - (toDate(a.expiresAt)?.getTime() || 0));
        setLots([...activeLots, ...historyLots]);
      } catch (err) {
        if (!cancelled) setError('課金情報の読み込みに失敗しました。');
        console.error('Failed to load billing state:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [isActive, user?.uid]);

  async function callBilling(action, body) {
    if (!BILLING_API_BASE) {
      setError('決済は現在準備中です。');
      return;
    }
    setError('');
    setBusy(action + (body?.plan || body?.pack || ''));
    try {
      const idToken = await user.getIdToken();
      const resp = await fetch(`${BILLING_API_BASE}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.url) {
        throw new Error(data?.error?.message || '決済セッションの作成に失敗しました。');
      }
      window.location.assign(data.url);
    } catch (err) {
      setError(err.message || '決済処理に失敗しました。');
      setBusy('');
    }
  }

  const lotUsageByApp = useMemo(() => buildLotUsageByApp(usageEvents), [usageEvents]);
  const appLegend = useMemo(() => getAppLegend(lotUsageByApp, lots), [lotUsageByApp, lots]);

  if (IS_MOCK_DEMO) return null;

  const status = subscription?.status || null;
  const isActiveSub = status === 'active' || status === 'trialing';
  const canBuyPacks = status === 'active'; // matches backend pack gating (active only)
  const plan = subscription?.plan && subscription.plan !== 'free' ? subscription.plan : null;
  const totalRemaining = lots.filter((l) => l.status === 'active').reduce((s, l) => s + (l.remainingCredits || 0), 0);
  const statusBadge = status ? STATUS_LABELS[status] : null;
  const apiReady = Boolean(BILLING_API_BASE);
  const toggleLot = (lotId) => {
    setExpandedLotIds((prev) => {
      const next = new Set(prev);
      if (next.has(lotId)) next.delete(lotId);
      else next.add(lotId);
      return next;
    });
  };

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-400 font-bold uppercase tracking-wider">プラン / Billing</div>
        {statusBadge && (
          <span className={`text-[10px] px-2 py-0.5 rounded border ${statusBadge.cls}`}>{statusBadge.text}</span>
        )}
      </div>

      {loading ? (
        <div className="text-[10px] text-gray-500">読み込み中...</div>
      ) : (
        <>
          {/* Current plan + renewal */}
          {plan ? (
            <div className="flex items-end justify-between">
              <div>
                <div className="text-sm font-bold text-white capitalize">{plan} プラン</div>
                {subscription?.cancelAtPeriodEnd && (
                  <div className="text-[10px] text-amber-400 mt-0.5">期間末に解約予定</div>
                )}
              </div>
              <div className="text-[10px] text-gray-500 text-right">
                {status === 'past_due'
                  ? <>猶予期限: {fmtDate(subscription?.gracePeriodEndsAt)}</>
                  : <>次回更新: {fmtDate(subscription?.currentPeriodEnd)}</>}
              </div>
            </div>
          ) : (
            <div className="text-[11px] text-gray-400">
              有料プラン未加入です。Raw 保存・閲覧・Export は引き続きご利用いただけます。
            </div>
          )}

          {/* Total credits: lot breakdown when available, else users summary
              fallback so lot-less (manual/admin-granted) users still see a balance. */}
          {lots.length > 0 ? (
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <div className="text-lg font-black text-white flex items-baseline gap-1">
                  <span className="text-xs font-medium text-gray-400 mr-1">残クレジット</span>
                  {totalRemaining}
                </div>
                <CreditUsageLegend items={appLegend} />
              </div>
              <div className="space-y-1">
                {lots.map((l) => (
                  <CreditLotRow
                    key={l.id}
                    lot={l}
                    usageByApp={lotUsageByApp.get(l.id)}
                    loadingUsage={loadingUsage}
                    expanded={expandedLotIds.has(l.id)}
                    onToggle={() => toggleLot(l.id)}
                  />
                ))}
              </div>
            </div>
          ) : (userData?.grantedCredits > 0) ? (
            <div className="text-lg font-black text-white flex items-baseline gap-1">
              <span className="text-xs font-medium text-gray-400 mr-1">残クレジット</span>
              {Math.max(0, (userData.grantedCredits || 0) - (userData.usedCredits || 0))}
              <span className="text-[10px] font-medium text-gray-500">/ {userData.grantedCredits || 0}</span>
            </div>
          ) : null}

          {error && <div className="text-[10px] text-rose-400">{error}</div>}
          {!apiReady && (
            <div className="text-[10px] text-amber-400/80">決済導線は準備中です（VITE_BILLING_API_BASE 未設定）。</div>
          )}

          {/* Active subscriber: portal + credit packs */}
          {isActiveSub ? (
            <div className="space-y-2">
              <button
                type="button"
                disabled={!apiReady || busy === 'create-portal-session'}
                onClick={() => callBilling('create-portal-session')}
                className="w-full py-1.5 rounded text-xs font-bold transition-all bg-gray-700 text-gray-200 border border-gray-600 hover:bg-gray-600 disabled:opacity-50"
              >
                {busy === 'create-portal-session' ? '...' : 'カード変更・解約・プラン変更（Stripe）'}
              </button>
              <p className="text-[10px] text-gray-500">プラン変更は次回更新時に自動で切り替わります。</p>
              {canBuyPacks && (
              <>
              <div className="text-[10px] text-gray-500 font-medium pt-1">クレジットパックを追加購入</div>
              <div className="grid grid-cols-3 gap-1.5">
                {CREDIT_PACKS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    disabled={!apiReady || busy === `create-credit-pack-checkout-session${p.key}`}
                    onClick={() => callBilling('create-credit-pack-checkout-session', { pack: p.key })}
                    className="flex flex-col items-center py-2 rounded text-[10px] font-bold transition-all bg-indigo-900/30 text-indigo-300 border border-indigo-700/50 hover:bg-indigo-900/50 disabled:opacity-50"
                  >
                    <span>{p.label}</span>
                    <span className="text-gray-400">{p.price}</span>
                    <span className="text-[9px] text-gray-500">+{p.credits}cr</span>
                  </button>
                ))}
              </div>
              </>
              )}
            </div>
          ) : (
            /* Not subscribed (or past_due/canceled): show plans / recovery */
            <div className="space-y-1.5">
              {status === 'past_due' && (
                <div className="text-[10px] text-amber-300 bg-amber-900/20 border border-amber-800/50 rounded px-2 py-1">
                  お支払いを確認できませんでした。猶予期間内にカードをご確認ください。
                </div>
              )}
              <div className="grid grid-cols-3 gap-1.5">
                {SUBSCRIPTION_PLANS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    disabled={!apiReady || busy === `create-checkout-session${p.key}`}
                    onClick={() => callBilling('create-checkout-session', { plan: p.key })}
                    className="flex flex-col items-center py-2 rounded text-[11px] font-bold transition-all bg-blue-900/30 text-blue-300 border border-blue-700/50 hover:bg-blue-900/50 disabled:opacity-50"
                  >
                    <span>{p.label}</span>
                    <span className="text-gray-400 text-[10px]">{p.price}</span>
                    <span className="text-[9px] text-gray-500">{p.credits}cr/月</span>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-gray-500 leading-relaxed">
                月額プランは選択後、Stripe の決済画面に進みます。各更新日に同一料金で自動更新され、いつでも解約できます（解約後も期間末まで利用可）。詳細は{' '}
                <a href="/tokushoho" className="text-indigo-400 hover:text-indigo-300 underline">特定商取引法に基づく表記</a>
                ・
                <a href="/terms" className="text-indigo-400 hover:text-indigo-300 underline">利用規約</a>
                をご確認ください。
              </p>
              {status === 'past_due' && (
                <button
                  type="button"
                  disabled={!apiReady || busy === 'create-portal-session'}
                  onClick={() => callBilling('create-portal-session')}
                  className="w-full py-1.5 rounded text-xs font-bold transition-all bg-amber-700/40 text-amber-200 border border-amber-600/50 hover:bg-amber-700/60 disabled:opacity-50"
                >
                  カード情報を更新（Stripe）
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CreditUsageLegend({ items }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[9px] font-medium text-gray-500">
      {items.map((app) => (
        <span key={app.key} className="inline-flex items-center gap-1">
          <span className={`h-1.5 w-1.5 rounded-sm ${app.color}`} />
          {app.label}
        </span>
      ))}
    </div>
  );
}

function CreditLotRow({ lot, usageByApp, loadingUsage, expanded, onToggle }) {
  const { remaining, used, granted } = getLotCreditBreakdown(lot);
  const knownSegments = Array.from(usageByApp?.values?.() || []);
  const knownUsed = getLotKnownUsed(usageByApp);
  const unclassifiedCredits = Math.max(0, used - knownUsed);
  const segments = unclassifiedCredits > 0
    ? [...knownSegments, { ...OTHER_APP_USAGE, credits: unclassifiedCredits, actions: new Map() }]
    : knownSegments;
  const usedRatio = granted > 0 ? Math.min(100, (used / granted) * 100) : 0;
  const segmentTotal = knownUsed + unclassifiedCredits;

  const isHistory = lot.status !== 'active';
  return (
    <div className={`overflow-hidden rounded border ${isHistory ? 'border-gray-800 opacity-60' : 'border-gray-700'} bg-gray-900/50`}>
      <button
        type="button"
        onClick={onToggle}
        className="relative block w-full overflow-hidden px-2 py-1 text-left text-[10px] text-gray-400 hover:bg-gray-800/40 focus:outline-none focus:ring-1 focus:ring-blue-500/40"
        aria-expanded={expanded}
      >
        <div className="relative z-10 flex items-center justify-between gap-2">
          <div className="min-w-0 truncate">
            <span className="font-bold text-gray-300">{lotSourceLabel(lot.source)}</span>
            {lot.status === 'depleted' && <span className="ml-1 text-[9px] text-gray-600 border border-gray-700 rounded px-1">使用済み</span>}
            {lot.status === 'expired' && <span className="ml-1 text-[9px] text-gray-600 border border-gray-700 rounded px-1">期限切れ</span>}
            <span className="ml-1">
              残り <span className="font-bold text-white">{formatCredits(remaining)}</span>
            </span>
            <span className="mx-1 text-gray-600">/</span>
            <span>
              使用済み <span className="text-gray-400">{formatCredits(used)}</span>
            </span>
            <span className="mx-1 text-gray-600">/</span>
            <span>
              付与 <span className="text-gray-400">{formatCredits(granted)}</span>
            </span>
          </div>
          <span className="shrink-0 text-gray-500">期限 {fmtDate(lot.expiresAt)}</span>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-1 bg-gray-800">
          <div className="flex h-full" style={{ width: `${usedRatio}%` }}>
            {loadingUsage && used > 0 ? (
              <div className="h-full w-full bg-gray-600" />
            ) : segments.length > 0 ? (
              segments.map((segment) => (
                <div
                  key={segment.key}
                  className={`${segment.color} h-full`}
                  style={{ width: `${segmentTotal > 0 ? (segment.credits / segmentTotal) * 100 : 0}%` }}
                  title={`${segment.label}: ${formatCredits(segment.credits)} credits`}
                />
              ))
            ) : null}
          </div>
        </div>
      </button>
      {expanded && (
        <CreditLotUsageDetail
          segments={segments}
          loadingUsage={loadingUsage}
          used={used}
        />
      )}
    </div>
  );
}

function CreditLotUsageDetail({ segments, loadingUsage, used }) {
  if (loadingUsage && used > 0) {
    return <div className="border-t border-gray-700 px-2 py-2 text-[10px] text-gray-500">利用内訳を読み込み中...</div>;
  }

  if (used <= 0) {
    return <div className="border-t border-gray-700 px-2 py-2 text-[10px] text-gray-500">この付与枠の利用はありません。</div>;
  }

  return (
    <div className="space-y-2 border-t border-gray-700 px-2 py-2">
      {segments.map((segment) => (
        <CreditAppUsageDetail key={segment.key} appSegment={segment} />
      ))}
    </div>
  );
}

function CreditAppUsageDetail({ appSegment }) {
  const actions = Array.from(appSegment.actions?.values?.() || []);
  const actionTotal = actions.reduce((sum, action) => sum + action.credits, 0);

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2 text-[10px]">
        <span className="inline-flex items-center gap-1 font-bold text-gray-300">
          <span className={`h-1.5 w-1.5 rounded-sm ${appSegment.color}`} />
          {appSegment.label}
        </span>
        <span className="text-gray-500">{formatCredits(appSegment.credits)}cr</span>
      </div>
      {actions.length > 0 ? (
        <>
          <div className="flex h-1.5 overflow-hidden rounded-full bg-gray-800">
            {actions.map((action) => (
              <div
                key={action.key}
                className={`${action.color} h-full`}
                style={{ width: `${actionTotal > 0 ? (action.credits / actionTotal) * 100 : 0}%` }}
                title={`${action.label}: ${formatCredits(action.credits)} credits`}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-x-2 gap-y-0.5">
            {actions.map((action) => (
              <span key={action.key} className="inline-flex items-center gap-1 text-[9px] text-gray-500">
                <span className={`h-1.5 w-1.5 rounded-sm ${action.color}`} />
                {action.label} {formatCredits(action.credits)}
              </span>
            ))}
          </div>
        </>
      ) : (
        <div className="text-[9px] text-gray-600">詳細な用途は未分類です。</div>
      )}
    </div>
  );
}
